import { CompleteAddress } from '@dreryk/address-common-api';
import {
   HealthcareFacilityLocationCodeNotUniqueFault,
   HealthcareFacilityLocationUpdateCommand,
   HealthcareOrganizationPartContactData,
} from '@dreryk/egabinet-healthcare-provider-frontend-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import { commonTranslationKeys } from '@dreryk/react-common';
import { dispatchSnackbarPositiveEvent, ModalConfirmButton } from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { healthcareOrganizationTranslationKeys } from '../../../../translations';
import { mapScopeOfProvidedServicesFormBeanPartToParameters } from '../../shared/scopeOfProvidedServices/ScopeOfProvidedServicesInteractiveTable.utils';

import {
   HealthcareFacilityLocationUpdateFormBean,
   HealthcareFacilityLocationUpdateFormController,
} from './form/HealthcareFacilityLocationUpdateForm';

type HealthcareFacilityLocationUpdateModalConfirmButtonProps = Pick<
   HealthcareFacilityLocationUpdateFormController,
   'handleSubmit'
> & {
   readonly onDataChanged: VoidFunction;
   readonly setLocationCodeNotUniqueErrorMessage: VoidFunction;
};

export const HealthcareFacilityLocationUpdateModalConfirmButton = (
   props: HealthcareFacilityLocationUpdateModalConfirmButtonProps
): JSX.Element => {
   const { executeAsync: updateHealthcareFacilityLocation } = useCqrsCommand<
      void,
      HealthcareFacilityLocationUpdateFormBean,
      HealthcareFacilityLocationCodeNotUniqueFault
   >(
      (parameters) => {
         const command = new HealthcareFacilityLocationUpdateCommand();
         command.id = parameters.id;
         command.name = parameters.name;
         command.locationCode = parameters.locationCode!;
         command.specialtyId = parameters.specialty.id;
         command.usingHealthcareFacilityUnitAddressData = parameters.usingParentAddressData;

         if (!parameters.usingParentAddressData) {
            command.address = Choice.of<CompleteAddress>(
               CompleteAddress.builder()
                  .withTown(parameters.city!)
                  .withPostcode(parameters.postcode!)
                  .withStreet(parameters.street)
                  .withBuilding(parameters.house!)
                  .withFlat(parameters.flat)
                  .build()
            );
         }

         command.usingHealthcareFacilityUnitContactData = parameters.usingParentContactData;
         if (!parameters.usingParentContactData) {
            command.contactData = Choice.of<HealthcareOrganizationPartContactData>({
               phoneNumber: parameters.phoneNumber!,
               emailAddress: Choice.withUseDefaultIfNull(parameters.emailAddress),
            });
         }

         command.scopeOfProvidedServicesParametersList = mapScopeOfProvidedServicesFormBeanPartToParameters(parameters);

         return command;
      },
      {
         onSuccess: () => {
            dispatchSnackbarPositiveEvent(healthcareOrganizationTranslationKeys.healthcareFacilityLocationDataChanged);
            props.onDataChanged();
         },
         onFault: {
            'eGabinetHealthcareProvider:HealthcareFacilityLocationCodeNotUniqueFault': () =>
               props.setLocationCodeNotUniqueErrorMessage(),
         },
      }
   );

   return (
      <ModalConfirmButton
         label={commonTranslationKeys.save}
         onClick={props.handleSubmit(updateHealthcareFacilityLocation)}
      />
   );
};
